import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, X, Loader2 } from 'lucide-react';

/**
 * AddressSearch component with Nominatim autocomplete
 * Uses OpenStreetMap's free geocoding API
 */
const AddressSearch = ({ 
  onLocationSelect, 
  placeholder = "Rechercher une adresse...",
  defaultValue = "",
  className = "" 
}) => {
  const [query, setQuery] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Cameroon bounding box for focused search
  const CAMEROON_BOUNDS = {
    viewbox: '8.4,1.6,16.2,13.1', // west,south,east,north
    countrycodes: 'cm',
  };

  useEffect(() => {
    // Cleanup debounce on unmount
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const searchAddress = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: CAMEROON_BOUNDS.countrycodes,
        viewbox: CAMEROON_BOUNDS.viewbox,
        bounded: '1',
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'Accept-Language': 'fr',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Nominatim search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    const displayName = formatDisplayName(suggestion);
    setQuery(displayName);
    setSuggestions([]);
    setShowSuggestions(false);

    if (onLocationSelect) {
      onLocationSelect({
        latitude: parseFloat(suggestion.lat),
        longitude: parseFloat(suggestion.lon),
        address: displayName,
        city: suggestion.address?.city || suggestion.address?.town || suggestion.address?.village || '',
        raw: suggestion,
      });
    }
  };

  const formatDisplayName = (suggestion) => {
    const address = suggestion.address || {};
    const parts = [];

    if (address.road || address.pedestrian) {
      parts.push(address.road || address.pedestrian);
    }
    if (address.neighbourhood || address.suburb) {
      parts.push(address.neighbourhood || address.suburb);
    }
    if (address.city || address.town || address.village) {
      parts.push(address.city || address.town || address.village);
    }

    return parts.length > 0 ? parts.join(', ') : suggestion.display_name;
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onLocationSelect) {
      onLocationSelect(null);
    }
    inputRef.current?.focus();
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(e.target) &&
        !inputRef.current?.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#E53935]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full h-12 pl-11 pr-20 rounded-xl border border-slate-200 bg-white text-[#1A237E] placeholder-slate-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 transition-all"
          data-testid="address-search-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isLoading && (
            <Loader2 className="w-4 h-4 text-[#2196F3] animate-spin" />
          )}
          {query && !isLoading && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="address-clear-btn"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
          <Search className="w-4 h-4 text-slate-400" />
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          data-testid="address-suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                index === selectedIndex 
                  ? 'bg-[#E3F2FD]' 
                  : 'hover:bg-slate-50'
              }`}
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A237E] truncate">
                  {formatDisplayName(suggestion)}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {suggestion.address?.state || suggestion.address?.region || 'Cameroun'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && query.length >= 3 && suggestions.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-4 text-center">
          <p className="text-sm text-slate-500">Aucune adresse trouvée</p>
          <p className="text-xs text-slate-400 mt-1">Essayez avec un autre terme</p>
        </div>
      )}
    </div>
  );
};

export default AddressSearch;
